import { RefObject, useState, useRef, useEffect } from 'react'
import {
  Search,
  Mail,
  CheckCircle2,
  X,
  PanelLeft,
  MoreHorizontal,
  RotateCcw,
  StickyNote,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import WhatsAppBadge from '@/components/WhatsAppBadge'
import StatusFilterDropdown from '@/components/StatusFilterDropdown'
import AgentAssignmentDropdown from '@/components/AgentAssignmentDropdown'
import { stripHtml } from '@/lib/sanitizeHtml'
import OnlineIndicator from './OnlineIndicator'
import {
  getInitials,
  getAvatarColor,
  isUserOnline,
  formatRelativeTime,
} from './utils'
import type {
  ConversationListItem,
  Agent,
  StatusFilterType,
} from './types'

interface ConversationListProps {
  conversations: ConversationListItem[]
  selectedConversationId: number | null
  searchQuery: string
  onSearchChange: (query: string) => void
  onConversationClick: (id: number) => void
  isSidebarOpen: boolean
  onToggleSidebar: () => void
  // Status filter
  statusFilter: string
  stats: { open: number; closed: number; snoozed: number }
  onStatusChange: (status: StatusFilterType) => void
  // Multi-select
  selectedConversationIds: Set<number>
  onToggleSelection: (id: number) => void
  onToggleSelectAll: () => void
  onDeselectAll: () => void
  // Bulk actions
  agents: Agent[]
  currentAgentId: number
  onBulkAssign: (agentId: number) => void
  onBulkUnassign: () => void
  onBulkStatusChange: (status: 'closed' | 'open') => void
  // Infinite scroll
  loadMoreRef: RefObject<HTMLDivElement | null>
  isLoadingMore: boolean
  hasMore: boolean
  totalCount: number
  // Agent presence
  conversationPresence: Map<number, Array<{ id: number; name: string; avatar_url: string | null }>>
}

export default function ConversationList({
  conversations,
  selectedConversationId,
  searchQuery,
  onSearchChange,
  onConversationClick,
  isSidebarOpen,
  onToggleSidebar,
  statusFilter,
  stats,
  onStatusChange,
  selectedConversationIds,
  onToggleSelection,
  onToggleSelectAll,
  onDeselectAll,
  agents,
  currentAgentId,
  onBulkAssign,
  onBulkUnassign,
  onBulkStatusChange,
  loadMoreRef,
  isLoadingMore,
  hasMore,
  totalCount,
  conversationPresence,
}: ConversationListProps) {
  const isMultiSelectActive = selectedConversationIds.size > 0

  return (
    <div className="w-[420px] min-w-[270px] shrink bg-[#fdfdfd] border-r border-gray-200 flex flex-col">
      {/* Search Header */}
      <div className="h-14 flex items-center px-4 border-b border-gray-200 gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleSidebar}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <PanelLeft className="w-5 h-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {isSidebarOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
          </TooltipContent>
        </Tooltip>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Status Filter or Selection Bar */}
      <div className="px-4 py-2 border-b border-gray-100">
        {isMultiSelectActive ? (
          <MultiSelectBar
            selectedCount={selectedConversationIds.size}
            totalCount={conversations.length}
            agents={agents}
            currentAgentId={currentAgentId}
            onToggleSelectAll={onToggleSelectAll}
            onDeselectAll={onDeselectAll}
            onBulkAssign={onBulkAssign}
            onBulkUnassign={onBulkUnassign}
            onBulkStatusChange={onBulkStatusChange}
          />
        ) : (
          <StatusFilterDropdown
            currentStatus={statusFilter as StatusFilterType}
            stats={stats}
            onStatusChange={onStatusChange}
          />
        )}
      </div>

      {/* Conversation Items */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-sm">Nenhuma conversa</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {conversations.map((conv) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isActive={selectedConversationId === conv.id}
                isSelected={selectedConversationIds.has(conv.id)}
                isMultiSelectActive={isMultiSelectActive}
                presenceAgents={conversationPresence.get(conv.id)}
                onClick={() => onConversationClick(conv.id)}
                onToggleSelection={() => onToggleSelection(conv.id)}
              />
            ))}

            {/* Infinite scroll sentinel */}
            {!searchQuery && (
              <div ref={loadMoreRef} className="py-4">
                {isLoadingMore && (
                  <div className="flex justify-center">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {!hasMore && totalCount > 0 && (
                  <p className="text-center text-xs text-gray-400">
                    Todas as conversas carregadas
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// -- Sub-components --

interface MultiSelectBarProps {
  selectedCount: number
  totalCount: number
  agents: Agent[]
  currentAgentId: number
  onToggleSelectAll: () => void
  onDeselectAll: () => void
  onBulkAssign: (agentId: number) => void
  onBulkUnassign: () => void
  onBulkStatusChange: (status: 'closed' | 'open') => void
}

function MultiSelectBar({
  selectedCount,
  totalCount,
  agents,
  currentAgentId,
  onToggleSelectAll,
  onDeselectAll,
  onBulkAssign,
  onBulkUnassign,
  onBulkStatusChange,
}: MultiSelectBarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isDropdownOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDropdownOpen])

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onToggleSelectAll}
        className="shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors border-gray-800 bg-gray-800"
      >
        {selectedCount === totalCount ? (
          <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
            <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <span className="block w-2.5 h-0.5 bg-white rounded" />
        )}
      </button>
      <span className="text-sm text-gray-600 whitespace-nowrap">
        {selectedCount}/{totalCount} selecionadas
      </span>
      <div className="flex items-center gap-1.5 ml-auto">
        <AgentAssignmentDropdown
          agents={agents}
          currentAgentId={currentAgentId}
          onAssign={onBulkAssign}
          onUnassign={onBulkUnassign}
          triggerVariant="button"
        />
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="inline-flex items-center justify-center w-8 h-8 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
              <button
                onClick={() => {
                  onBulkStatusChange('closed')
                  setIsDropdownOpen(false)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <CheckCircle2 className="w-4 h-4" />
                Fechar conversas
              </button>
              <button
                onClick={() => {
                  onBulkStatusChange('open')
                  setIsDropdownOpen(false)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <RotateCcw className="w-4 h-4" />
                Reabrir conversas
              </button>
            </div>
          )}
        </div>
        <button
          onClick={onDeselectAll}
          className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

interface LastMessagePreviewProps {
  text: string
  isNote?: boolean
  hasUnread: boolean
}

function LastMessagePreview({ text, isNote, hasUnread }: LastMessagePreviewProps) {
  if (isNote) {
    return (
      <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 max-w-full">
        <StickyNote className="w-3 h-3 text-amber-700 shrink-0" />
        <span className="text-xs text-amber-900 truncate">{stripHtml(text)}</span>
      </span>
    )
  }

  return (
    <p
      className={`text-xs truncate block ${
        hasUnread ? 'text-gray-600' : 'text-gray-400'
      }`}
    >
      {stripHtml(text)}
    </p>
  )
}

interface ConversationItemProps {
  conversation: ConversationListItem
  isActive: boolean
  isSelected: boolean
  isMultiSelectActive: boolean
  presenceAgents?: Array<{ id: number; name: string; avatar_url: string | null }>
  onClick: () => void
  onToggleSelection: () => void
}

function ConversationItem({
  conversation: conv,
  isActive,
  isSelected,
  isMultiSelectActive,
  presenceAgents,
  onClick,
  onToggleSelection,
}: ConversationItemProps) {
  return (
    <button
      onClick={onClick}
      className={`group/conv w-full text-left p-3 hover:bg-gray-50 transition-colors cursor-pointer ${
        isActive ? 'bg-blue-50' : ''
      } ${isSelected ? 'bg-blue-50' : ''}`}
    >
      <div className="flex gap-3">
        <div className="relative shrink-0">
          {/* Checkbox: visible on hover or when multi-select is active */}
          <div
            className={`absolute inset-0 flex items-center justify-center z-10 ${
              isMultiSelectActive || isSelected
                ? 'opacity-100'
                : 'opacity-0 group-hover/conv:opacity-100'
            }`}
            onClick={(e) => {
              e.stopPropagation()
              onToggleSelection()
            }}
          >
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                isSelected
                  ? 'border-gray-800 bg-gray-800'
                  : 'border-gray-400 bg-white hover:border-gray-600'
              }`}
            >
              {isSelected && (
                <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>

          {/* Avatar: hidden on hover or when multi-select is active */}
          <div
            className={`${
              isMultiSelectActive || isSelected
                ? 'invisible'
                : 'group-hover/conv:invisible'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(
                conv.end_user.name
              )}`}
            >
              {getInitials(conv.end_user.name)}
            </div>
          </div>
          {!isMultiSelectActive && !isSelected && conv.unread_count > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center group-hover/conv:hidden">
              {conv.unread_count > 9 ? '9+' : conv.unread_count}
            </span>
          )}
          {!isMultiSelectActive && !isSelected && isUserOnline(conv.end_user.last_seen_at) && (
            <span className="group-hover/conv:hidden"><OnlineIndicator size="lg" /></span>
          )}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1.5 mb-0.5">
            {conv.priority === 'urgent' && <span className="w-2 h-2 rounded-full bg-red-500" title="Urgent" />}
            {conv.priority === 'high' && <span className="w-2 h-2 rounded-full bg-orange-500" title="High" />}
            {conv.source === 'email' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top">Via e-mail</TooltipContent>
              </Tooltip>
            )}
            {conv.source === 'whatsapp' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="shrink-0">
                    <WhatsAppBadge size="sm" className="text-green-600" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Via WhatsApp</TooltipContent>
              </Tooltip>
            )}
            <span
              className={`text-sm truncate block ${
                conv.unread_count > 0 ? 'font-semibold text-gray-900' : 'font-normal text-gray-500'
              }`}
            >
              {conv.end_user.name}
            </span>
          </div>
          {conv.last_message && (
            <LastMessagePreview
              text={conv.last_message}
              isNote={conv.last_message_is_note}
              hasUnread={conv.unread_count > 0}
            />
          )}
        </div>
        <div className="shrink-0 text-right">
          <span className="text-xs text-gray-400">
            {formatRelativeTime(conv.last_message_at)}
          </span>
          {presenceAgents && presenceAgents.length > 0 && (
            <div className="flex -space-x-1 mt-0.5 justify-end">
              {presenceAgents.slice(0, 3).map((presenceAgent) =>
                presenceAgent.avatar_url ? (
                  <img
                    key={presenceAgent.id}
                    src={presenceAgent.avatar_url}
                    alt={presenceAgent.name}
                    className="w-5 h-5 rounded-full object-cover ring-1 ring-white"
                    title={presenceAgent.name}
                  />
                ) : (
                  <div
                    key={presenceAgent.id}
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium ring-1 ring-white ${getAvatarColor(presenceAgent.name)}`}
                    title={presenceAgent.name}
                  >
                    {getInitials(presenceAgent.name)}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  )
}

